import { useCallback } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import ChevronLeft from "lucide-react/dist/esm/icons/chevron-left.js";
import Menu from "lucide-react/dist/esm/icons/menu.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@shared/ui/sheet/Sheet";
import { cn } from "@shared/lib/utils";
import { HeaderOverflowMenu } from "../shared/header-controls";
import { useLandingPath } from "@shared/hooks/use-site-feature-flags/useLandingPath";
import { useState } from "react";

// Tab roots — routes matching these are considered "root" level for that tab.
const TAB_ROOTS = ["/dashboard", "/track", "/ai-coaching", "/community", "/profile"];
const SUPPORTED_LANGUAGES = ["en", "ar", "de", "es", "fr"] as const;
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

const MENU_DESCRIPTION_FALLBACKS: Record<SupportedLanguage, string> = {
  en: "Navigate to dashboard sections.",
  ar: "انتقل إلى أقسام لوحة التحكم.",
  de: "Navigieren Sie zu den Dashboard-Bereichen.",
  es: "Navega por las secciones del panel.",
  fr: "Accédez aux sections du tableau de bord.",
};

const NO_MENU_ITEMS_FALLBACKS: Record<SupportedLanguage, string> = {
  en: "No menu items available.",
  ar: "لا توجد عناصر قائمة متاحة.",
  de: "Keine Menüeinträge verfügbar.",
  es: "No hay elementos de menú disponibles.",
  fr: "Aucun élément de menu disponible.",
};

function getSupportedLanguage(language?: string): SupportedLanguage {
  const baseLanguage = language?.split("-")[0];
  return SUPPORTED_LANGUAGES.includes(baseLanguage as SupportedLanguage)
    ? (baseLanguage as SupportedLanguage)
    : "en";
}

/**
 * Parent route of `pathname`: the path with its last segment removed, keeping a
 * leading language prefix. `/health-assistant/voice-log` -> `/health-assistant`,
 * `/ar/health-assistant/voice-log` -> `/ar/health-assistant`. Returns `null`
 * when the route has no parent (a single top-level segment, or the root), in
 * which case the caller falls back to browser history.
 */
export function getParentPath(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  let prefix = "";
  if (
    segments.length > 0 &&
    SUPPORTED_LANGUAGES.includes(segments[0] as SupportedLanguage)
  ) {
    prefix = `/${segments.shift()}`;
  }
  if (segments.length <= 1) return null;
  return `${prefix}/${segments.slice(0, -1).join("/")}`;
}

function isTabRoot(pathname: string, landingPath?: string): boolean {
  // Strip a 2-char language prefix (/ar, /en …) only when followed by / or EOL.
  const cleaned =
    pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
  return (
    cleaned === "/" ||
    cleaned === "" ||
    TAB_ROOTS.some((root) => cleaned === root || cleaned === root + "/") ||
    // The Home tab points at the first enabled feature (site feature flags).
    (landingPath !== undefined && (cleaned === landingPath || cleaned === landingPath + "/"))
  );
}

export interface MobileHeaderProps {
  title?: string;
  rightAction?: React.ReactNode;
  secondaryNavContent?: React.ReactNode;
}

export function MobileHeader({ title, rightAction, secondaryNavContent }: MobileHeaderProps) {
  const { t, i18n } = useTranslation("mobile");
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const landingPath = useLandingPath();
  const showBack = !isTabRoot(location.pathname, landingPath);
  const language = getSupportedLanguage(i18n.resolvedLanguage || i18n.language);
  const menuSide = language === "ar" ? "right" : "left";
  const menuDescription = t(
    "header.menuDescription",
    MENU_DESCRIPTION_FALLBACKS[language],
  );
  const noMenuItems = t(
    "header.noMenuItems",
    NO_MENU_ITEMS_FALLBACKS[language],
  );

  // Back goes UP the route hierarchy, not back through history: arriving on
  // /health-assistant/voice-log from a deep link and pressing Back must land on
  // /health-assistant, not leave the app.
  const handleBack = useCallback(() => {
    const parent = getParentPath(location.pathname);
    if (parent) {
      void navigate({ to: parent as never });
      return;
    }
    window.history.back();
  }, [location.pathname, navigate]);

  const handleMenuContentClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("a[href]")) {
      setMenuOpen(false);
    }
  }, []);

  return (
    <>
      <header
        role="banner"
        className="sticky top-0 z-40 flex h-14 items-center justify-between gap-2 border-b border-border bg-background/80 px-3 backdrop-blur-sm"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex min-w-[44px] items-center">
          {showBack ? (
            <button
              type="button"
              aria-label={t("header.back")}
              onClick={handleBack}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronLeft
                className="h-5 w-5 rtl:scale-x-[-1]"
                aria-hidden="true"
              />
            </button>
          ) : (
            <button
              type="button"
              aria-label={t("header.menu")}
              onClick={() => setMenuOpen(true)}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* A <span>, not a heading: the page owns the single <h1>. */}
        <span
          data-testid="mobile-header-title"
          className={cn(
            "block min-w-0 flex-1 truncate text-center text-base font-semibold text-foreground",
          )}
        >
          {title}
        </span>

        <div className="flex min-w-[44px] items-center justify-end">
          {rightAction ?? null}
          <HeaderOverflowMenu />
        </div>
      </header>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side={menuSide}
          className="flex w-[min(22rem,calc(100vw-3rem))] flex-col p-0"
          closeLabel={t("header.close")}
        >
          <SheetHeader className="border-b border-border px-4 py-4 pe-14">
            <SheetTitle className="text-start text-base">{t("header.menu")}</SheetTitle>
            <SheetDescription className="sr-only">
              {menuDescription}
            </SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col" onClick={handleMenuContentClick}>
            {secondaryNavContent ?? (
              <p className="p-4 text-sm text-muted-foreground">
                {noMenuItems}
              </p>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
